import { format, parseISO, intlFormat } from 'date-fns';
import { AppData } from 'services/apollo/core';

export const environment = {
  REACT_APP_GRAPHQL_URI: process.env.REACT_APP_GRAPHQL_URI,
  REACT_APP_STO: process.env.REACT_APP_STO,
  REACT_APP_STO_MODE: process.env.REACT_STO_MODE,
  REACT_APP_SHOW_STO_STYLING: process.env.REACT_APP_SHOW_STO_STYLING,
  // Overwrite with injected values from window.env if present
  ...(
    window as unknown as {
      env: {
        REACT_APP_GRAPHQL_URI?: string;
        REACT_APP_STO?: string;
        REACT_APP_STO_MODE?: string;
        REACT_APP_SHOW_STO_STYLING?: string;
      };
    }
  ).env,
};

export interface canSeeNDADataRequired {
  isStoNDAFlowEnabled: boolean;
  isNDASigned?: boolean;
  investorCountry?: string;
  appData: AppData;
  isBuyButtonEnabled?: boolean;
  isBuyStoEnabled?: boolean;
}

export const canSeeNDAData = (data: canSeeNDADataRequired): boolean => {
  const { isNDAFlowEnabled, accreditationRequiringCountries, isRegDRegSSeparationEnabled } = data.appData;
  const { isNDASigned, isStoNDAFlowEnabled, investorCountry } = data;
  if (!isNDAFlowEnabled || !isStoNDAFlowEnabled) {
    return true;
  }
  if (isNDASigned || !accreditationRequiringCountries.includes(investorCountry ?? '')) {
    return true;
  }
  if (isRegDRegSSeparationEnabled && !accreditationRequiringCountries.includes(investorCountry ?? '')) {
    return true;
  }

  return false;
};

export const showBuyButton = (data: canSeeNDADataRequired): boolean => {
  const { isBuyButtonEnabled, isBuyStoEnabled } = data;
  const { isRegDRegSSeparationEnabled, isBuyStoWhitelistPerInvestorEnabled } = data.appData;

  if (!isBuyButtonEnabled) {
    return false;
  }
  if (isRegDRegSSeparationEnabled) {
    return true;
  }
  if (isBuyStoWhitelistPerInvestorEnabled && !isBuyStoEnabled) {
    return false;
  }
  return true;
};

export const isMulti: () => boolean = () =>
  // Assume 'MULTI' by default
  environment.REACT_APP_STO_MODE === 'MULTI' || !environment.REACT_APP_STO_MODE;

export const isSingle: () => boolean = () => environment.REACT_APP_STO_MODE === 'SINGLE';

export const formatDate: (date: string) => string = (date) => format(parseISO(date), 'MMMM dd yyyy');

export const unixTimeToDate = (timeStamp: string): string =>
  timeStamp ? new Date(Number(timeStamp)).toDateString() : '';

export const isDev: () => boolean = () => process.env.NODE_ENV === 'development';

export const unixTimeToLocalDate = (timeStamp: string | undefined, locale: string): string =>
  timeStamp
    ? intlFormat(
        parseInt(timeStamp, 10) ?? 0,
        {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        },
        {
          locale,
        },
      )
    : '';

/** Split str as { key, args } for use in t(key, args) if str is api arguments string. Split on \, */
export const tArgs = (str: string): { key: string; args: { [key: string]: string } } => {
  if (str.startsWith('api-arg-')) {
    const [msg, args] = str.split('\\,');
    return { key: msg, args: JSON.parse((args ?? '{}').replaceAll('\\"', '"')) };
  }
  return { key: str, args: {} };
};

/**
 * Returns the unique elements contained in both arrays (unsorted). If comparing objects, their references are compared,
 * therefore should not be used for objects.
 * @param array1
 * @param array2
 */
export const intersectArrays = <T>(array1: T[], array2: T[]): T[] => {
  const intersectionDuplicates = array1.filter((value) => array2.includes(value));
  const intersection = arrayRemoveDuplicates(intersectionDuplicates);
  return intersection;
};

/**
 * Returns the unique elements of an array. It should not be used for removing duplicate objects, since their references are compared.
 * If a property is provided, it will remove objects that have duplicates of the property.
 * @param array
 * @param property - optional
 */
export const arrayRemoveDuplicates = <T>(array: T[], property?: keyof T): T[] => {
  const map = new Map();
  for (const item of array) {
    if (property) {
      map.set(item[property], item);
    } else {
      map.set(item, item);
    }
  }
  return Array.from(map.values());
};

export interface GqlError {
  graphQLErrors: [
    {
      message: string;
      path: string[];
      extensions: {
        translationKey: string;
        code: string;
      };
    },
  ];
  message: string;
}
export const getTranslationKeyOfApiError = (error: GqlError): string => {
  const { translationKey } = error?.graphQLErrors[0]?.extensions;
  return translationKey || error.message;
};
